import { useEffect, useImperativeHandle, forwardRef, useState, useRef, useCallback } from 'react';
import brokerConfig from '../../config/brokerConfig';

/**
 * ZohoChatbot Component
 * 
 * Integrates Zoho SalesIQ chat widget with aggressive visitor identification.
 */

const ZohoChatbot = forwardRef(({ open, onReady, clientId, clientName, clientEmail, isLoginPage, autoOpen }, ref) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const isScriptLoaded = useRef(false);
  const lastIdentifiedData = useRef({ id: '', name: '', email: '' });
  const cleanupDelayMs = 350;

  const performDeepCleanup = useCallback(() => {
    console.log('Zoho SalesIQ: Performing deep cleanup/purge...');

    try {
      const salesiq = window.$zoho?.salesiq || window.$zsiq;
      salesiq?.chat?.close?.();
      salesiq?.floatwindow?.visible?.('hide');
      salesiq?.floatbutton?.visible?.('hide');
      salesiq?.visitor?.reset?.();
      salesiq?.visitor?.clear?.();
      salesiq?.reset?.();
      salesiq?.logout?.();
    } catch (error) {
      console.warn('Zoho SalesIQ reset failed before cleanup:', error);
    }

    const scripts = document.querySelectorAll('script#zsiqscript, script[src*="salesiq"], script[src*="zoho"]');
    scripts.forEach(script => { try { script.remove(); } catch (e) {} });

    const zohoElements = document.querySelectorAll(
      '[id^="zsiq"], [class^="zsiq"], [id*="zsiq"], [class*="zsiq"], [id*="zoho"], [class*="zoho"], [class*="zls"]'
    );
    zohoElements.forEach(el => { try { el.remove(); } catch (e) {} });

    const iframes = document.getElementsByTagName('iframe');
    for (let i = iframes.length - 1; i >= 0; i--) {
      const iframe = iframes[i];
      const iframeMarker = `${iframe.id || ''} ${iframe.name || ''} ${iframe.src || ''}`.toLowerCase();
      if (iframeMarker.includes('zsiq') || iframeMarker.includes('zoho') || iframeMarker.includes('salesiq')) {
        try { iframe.remove(); } catch (e) {}
      }
    }

    ['$zoho', '$zsiq', 'ZohoSalesIQ', 'SalesIQ', 'zsiq_chat', 'zsiq_float'].forEach(globalName => {
      try { window[globalName] = undefined; delete window[globalName]; } catch (e) {}
    });

    try {
      Object.keys(localStorage).forEach(key => {
        const normalizedKey = key.toLowerCase();
        if (normalizedKey.includes('zsiq') || normalizedKey.includes('salesiq') || normalizedKey.includes('zoho') || normalizedKey.startsWith('siq_')) {
          localStorage.removeItem(key);
        }
      });
    } catch (e) {}

    try {
      Object.keys(sessionStorage).forEach(key => {
        const normalizedKey = key.toLowerCase();
        if (normalizedKey.includes('zsiq') || normalizedKey.includes('salesiq') || normalizedKey.includes('zoho') || normalizedKey.startsWith('siq_')) {
          sessionStorage.removeItem(key);
        }
      });
    } catch (e) {}

    try {
      document.cookie.split(';').forEach(cookie => {
        const name = cookie.split('=')[0].trim();
        const normalizedName = name.toLowerCase();
        if (normalizedName.includes('zsiq') || normalizedName.includes('zoho') || normalizedName.includes('salesiq')) {
          const expires = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';
          document.cookie = `${name}=; ${expires}; path=/`;
          document.cookie = `${name}=; ${expires}; path=/; domain=${window.location.hostname}`;
          document.cookie = `${name}=; ${expires}; path=/; domain=.${window.location.hostname}`;
        }
      });
    } catch (e) {}

    isScriptLoaded.current = false;
    lastIdentifiedData.current = { id: '', name: '', email: '' };
    setIsInitialized(false);
  }, []);

  const cleanupAndWait = useCallback(() => {
    performDeepCleanup();
    return new Promise(resolve => {
      setTimeout(resolve, cleanupDelayMs);
    });
  }, [performDeepCleanup]);

  const getVisitorData = useCallback(() => {
    return {
      id: clientId || localStorage.getItem('login_id') || '',
      name: clientName || localStorage.getItem('login_name') || '',
      email: clientEmail || localStorage.getItem('login_email') || ''
    };
  }, [clientId, clientName, clientEmail]);

  const applyIdentification = useCallback((data) => {
    if (!window.$zoho || !window.$zoho.salesiq || !window.$zoho.salesiq.visitor) {
      console.warn('Zoho SalesIQ visitor API not available yet');
      return false;
    }

    const { id, name, email } = data || getVisitorData();

    try {
      console.log('Zoho SalesIQ: Setting identity...', { id, name });

      const visitor = window.$zoho.salesiq.visitor;

      // 1. Set ID first (Always force String to avoid backend crashes)
      if (id) {
        visitor.id(String(id));
      }

      // 2. Set Name
      if (name) {
        visitor.name(String(name));
      } else if (!id) {
        visitor.name('Visitor');
      }

      // 3. Set Email
      if (email) {
        visitor.email(String(email));
      }

      // 4. Set custom info fields to trigger UI refresh
      const configuredVisitorInfo = brokerConfig.support?.visitorInfo || {};
      const visitorInfo = {
        Lead_Source1: configuredVisitorInfo.Lead_Source1 || "MAIN WEBSITE (HOT)",
        Lead_Source_2: configuredVisitorInfo.Lead_Source_2 || "ChatBot",
        CM_Lead_Source_1: configuredVisitorInfo.CM_Lead_Source_1 || "MAIN WEBSITE (HOT)",
        CM_Lead_Source_2: configuredVisitorInfo.CM_Lead_Source_2 || brokerConfig.support?.leadSource2,
        Client_Id: id ? String(id) : '',
        Name: name ? String(name) : 'Visitor',
        Client_Name: name ? String(name) : 'Visitor',
        Email: email ? String(email) : '',
        Visitor_Type: isLoginPage ? 'pre_login' : 'logged_in'
      };

      visitor.info(visitorInfo);

      return true;
    } catch (error) {
      console.error('Error applying Zoho identification:', error);
      return false;
    }
  }, [getVisitorData, isLoginPage]);

  const openChat = () => {
    const tryOpen = () => {
      try {
        if (!window.$zoho || !window.$zoho.salesiq) {
          return false;
        }

        const siq = window.$zoho.salesiq;

        const floatEl =
          document.getElementById('zsiq_float') ||
          document.querySelector('[id^="zsiq_float"]') ||
          document.querySelector('[id*="zsiq"][id*="float"]') ||
          document.querySelector('[class*="zsiq"][class*="float"]:not([style*="none"])');
        if (floatEl) {
          floatEl.click();
          return true;
        }

        if (siq.floatwindow && typeof siq.floatwindow.visible === 'function') {
          siq.floatwindow.visible('show');
          return true;
        }
        if (siq.floatbutton && typeof siq.floatbutton.visible === 'function') {
          siq.floatbutton.visible('show');
          return true;
        }
        if (siq.chat && typeof siq.chat.open === 'function') {
          siq.chat.open();
          return true;
        }

        return false;
      } catch (error) {
        console.error('Zoho SalesIQ tryOpen error:', error);
        return false;
      }
    };

    console.log('Zoho SalesIQ: Attempting to open chat...');
    
    const tryOpenWithRetries = (attemptsLeft) => {
      if (tryOpen()) {
        console.log('Zoho SalesIQ: Successfully opened chat UI.');
        return true;
      }
      
      if (attemptsLeft > 0) {
        console.log(`Zoho SalesIQ: Not ready, retrying in 1000ms... (${attemptsLeft} attempts left)`);
        setTimeout(() => tryOpenWithRetries(attemptsLeft - 1), 1000);
      } else {
        console.error('Zoho SalesIQ: Failed to open chat after multiple attempts.');
      }
    };

    tryOpenWithRetries(10);
  };

  useImperativeHandle(ref, () => ({
    openChat,
    identify: (data) => applyIdentification(data),
    isInitialized: () => isInitialized,
    cleanup: cleanupAndWait
  }));

  // Effect 1: Inject script when open is true
  useEffect(() => {
    if (!open || isScriptLoaded.current) return;

    const widgetCode = (isLoginPage && brokerConfig.support?.loginWidgetCode)
      ? brokerConfig.support.loginWidgetCode
      : brokerConfig.support?.widgetCode;

    if (!widgetCode) {
      console.error('Zoho Widget Code missing for current broker configuration');
      return;
    }

    // Small delay to let the DOM settle before injecting
    const injectTimer = setTimeout(() => {
      try {
        console.log(`Zoho SalesIQ: Initializing with code: ${widgetCode}`);
        window.$zoho = window.$zoho || {};
        window.$zoho.salesiq = {
          ready: function () {
            console.log('Zoho SalesIQ: Received ready signal. Checking API population...');
            
            // The ready callback fires when the script loads, but modules load asynchronously
            const checkAPIPopulated = (attempts) => {
              const siq = window.$zoho.salesiq;
              const isPopulated = (siq.visitor || siq.chat || siq.floatwindow);
              
              if (isPopulated) {
                console.log('Zoho SalesIQ: API modules populated. Ready to use.');
                setIsInitialized(true);
                if (onReady) onReady();
              } else if (attempts > 0) {
                setTimeout(() => checkAPIPopulated(attempts - 1), 200);
              } else {
                console.warn('Zoho SalesIQ: Ready signal received but API modules never appeared.');
                setIsInitialized(true);
              }
            };
            
            checkAPIPopulated(25); 
          }
        };

        if (!document.getElementById('zsiqscript')) {
          const script = document.createElement('script');
          script.id = 'zsiqscript';
          script.defer = true;
          const domain = brokerConfig.support?.domain || 'salesiq.zohopublic.com';
          script.src = `https://${domain}/widget?wc=${widgetCode}`;
          document.body.appendChild(script);
          isScriptLoaded.current = true;
        }
      } catch (error) {
        console.error('Error initializing Zoho chat:', error);
      }
    }, cleanupDelayMs);

    return () => {
      clearTimeout(injectTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, onReady, isLoginPage]); // DO NOT add clientId or clientName here!

  // Effect 4: Deep cleanup ONLY when component unmounts at end of session
  useEffect(() => {
    return () => {
      performDeepCleanup();
    };
  }, [performDeepCleanup]);

  // Effect 2: Update visitor identification when initialized or when visitor data changes
  useEffect(() => {
    if (!isInitialized) return;

    const data = getVisitorData();

    // Check if identity data has actually changed or hasn't been set yet
    const hasDataChanged =
      data.id !== lastIdentifiedData.current.id ||
      data.name !== lastIdentifiedData.current.name ||
      data.email !== lastIdentifiedData.current.email;

    if (!hasDataChanged) return;

    console.log('Zoho SalesIQ: Visitor identity changed or initialized, applying:', data);

    // Delay identification slightly to allow the widget to settle
    const timer = setTimeout(() => {
      const success = applyIdentification(data);
      if (success) {
        lastIdentifiedData.current = data;
      }
    }, 1500);

    return () => clearTimeout(timer);
  }, [isInitialized, getVisitorData, applyIdentification]);

  // Effect 3: Auto-open when initialized if requested
  useEffect(() => {
    if (isInitialized && autoOpen) {
      console.log('Zoho SalesIQ: Auto-opening chat as requested');
      // Small delay to ensure all Zoho sub-components are ready
      const timer = setTimeout(() => {
        openChat();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isInitialized, autoOpen]);

  return null;
});

ZohoChatbot.displayName = 'ZohoChatbot';

export default ZohoChatbot;
